import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { identityApi } from '@/api/identity.api';
import { PortalDataList } from './PortalDataList';
import { showApiError } from '@/utils/apiError';

export function VendorPortalSections() {
  const { pathname } = useLocation();
  const section = pathname.split('/').pop() ?? 'dashboard';

  if (section === 'rfqs') return <VendorRfqsSection />;
  if (section === 'pos') return <VendorPosSection />;
  if (section === 'bills' || section === 'payments') return <VendorBillsSection />;
  if (section === 'performance') return <VendorPerformanceSection />;
  if (section === 'quotations' || section === 'documents') {
    return <PortalDataList title={section} items={[]} emptyMessage="Use RFQs to submit quotations." />;
  }
  return <VendorRfqsSection />;
}

function VendorRfqsSection() {
  const [items, setItems] = useState<Array<{ id: number; primary: string; meta?: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.vendorRfqs()
      .then((rows) => setItems(rows.map((r) => ({
        id: r.id,
        primary: r.rfqNumber,
        meta: r.responseStatus,
      }))))
      .catch((e) => showApiError(e, 'Could not load RFQs'))
      .finally(() => setLoading(false));
  }, []);

  return <PortalDataList title="RFQs" loading={loading} items={items} emptyMessage="No RFQs yet." />;
}

function VendorPosSection() {
  const [items, setItems] = useState<Array<{ id: number; primary: string; meta?: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.vendorPurchaseOrders()
      .then((rows) => setItems(rows.map((r) => ({
        id: r.id,
        primary: r.poNumber,
        meta: r.poStatus,
      }))))
      .catch((e) => showApiError(e, 'Could not load purchase orders'))
      .finally(() => setLoading(false));
  }, []);

  return <PortalDataList title="Purchase orders" loading={loading} items={items} emptyMessage="No purchase orders yet." />;
}

function VendorBillsSection() {
  const [items, setItems] = useState<Array<{ id: number; primary: string; meta?: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.vendorInvoices()
      .then((rows) => setItems(rows.map((r) => ({
        id: r.id,
        primary: r.billNumber,
        meta: r.billStatus,
      }))))
      .catch((e) => showApiError(e, 'Could not load bills'))
      .finally(() => setLoading(false));
  }, []);

  return <PortalDataList title="Vendor bills" loading={loading} items={items} emptyMessage="No bills yet." />;
}

function VendorPerformanceSection() {
  const [score, setScore] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.vendorPerformance()
      .then((data) => setScore(data.compositeScore != null ? String(data.compositeScore) : '—'))
      .catch((e) => showApiError(e, 'Could not load performance'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalDataList
      title="Performance score"
      loading={loading}
      items={score ? [{ id: 1, primary: 'Composite score', meta: score }] : []}
      emptyMessage="No scorecard data yet."
    />
  );
}
